<script lang="jsx">
import { useRoute } from 'vue-router'
import {
  VApp,
  VMain,
  VSnackbar
} from 'vuetify/components'

import * as layouts from './layouts'
import { useAuth } from './plugins/auth'
import { getMobileAuthToken } from '~/shared/mobile-auth-token.js'
import { registeredDialogs } from '~/ui/elements/ProgrammaticDialog'

export default {
  name: 'LilypadApp',
  setup () {
    const snackMessage = ref(null)
    const snackMessageColor = ref(null)

    const showNotification = (text, color) => {
      snackMessage.value = text
      snackMessageColor.value = color
    }

    const auth = useAuth()
    const route = useRoute()
    const { appContext } = getCurrentInstance()

    // Providing so the Composition API has access
    provide('showNotification', showNotification)
    // Adding to globalProperties so templates and the Options API have access
    appContext.config.globalProperties.$showNotification = showNotification

    const Layout = computed(() => {
      /**
       * Embed mobile routes use a cookie-based auth token to bypass
       * the authentication and authorization process via SSO/CAS ($auth).
       */
      if (route.meta?.mobile && getMobileAuthToken()) return layouts.embed

      if (auth.loading) return layouts.loading
      if (auth.hasError) return layouts.error

      // In some cases we want routes to be rendered without the default layout.
      // An example of this is the embeddable pricing tab.
      if (route.meta?.layout === 'none') return layouts.embed

      return layouts.default
    })

    return () => (
      <VApp>
        <VMain>
          <Layout.value />
        </VMain>
        <VSnackbar
          modelValue={Boolean(snackMessage.value)}
          timeout={4000}
          onUpdate:modelValue={() => { snackMessage.value = null }}
          color={snackMessageColor.value}
        >
          {snackMessage.value}
        </VSnackbar>
        {Object.values(registeredDialogs.value).map(dialog => h(dialog))}
      </VApp>
    )
  }
}
</script>

<style lang="scss">
@use '~/styles/mixins' as *;

html.layout--fullscreen {
  overflow-y: hidden;
}

.v-main > .vue-portal-target > * {
  @include z-index(dialog);
}

// Updates to the VTooltip global styling based on Material specs.
// @see https://material.io/components/tooltips#specs
.v-tooltip .v-overlay__content {
  font-size: 10px;
  line-height: 24px;
  padding: 0 8px;
}

[aria-busy="true"],
.v-application a[aria-busy="true"] {
  cursor: progress;
  pointer-events: all;
}
</style>
